import * as React from 'react';
import FullCalendar from '@fullcalendar/react'; // must go before plugins
import dayGridPlugin from '@fullcalendar/daygrid'; // a plugin!
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import Modal from '@/components/modal';
import * as moment from 'moment';

interface EventProps {
    events?: any;
    siteSettings: object;
}

interface EventState {
    modalVisible: boolean;
    currentEvent: object;
}

/**
 * Adds an event calendar to the page.  Opens events in a modal
 */
export class EventCalendar extends React.Component<EventProps, EventState> {
    calendarComponentRef: any = React.createRef();

    static defaultProps: Partial<EventProps> = {
        events: [],
    };

    public state: EventState = {
        modalVisible: false,
        currentEvent: {},
    };

    /**
     * Decides what calendar view we should show based on the screen size
     */
    public getHeaderProps = (): object => ({
        left: '',
        center: 'prev,title,next',
        right: window.innerWidth < 765 ? '' : 'dayGridMonth,listWeek',
    });

    public handleWindowResize = (): void => {
        const calendar = this.calendarComponentRef.current.getApi();
        calendar.changeView(window.innerWidth < 765 ? 'listWeek' : 'dayGridMonth');
        calendar.setOption('header', this.getHeaderProps());
    };

    /**
   * Renders the content of what shows on the calendar
   * Empty <a> tag required to work on mobile week view so don't remove that, or you'll break mobile
   * @param eventInfo
   */
    public renderEventContent(eventInfo) {
        return (
            <>
              <a href={eventInfo.event.url}></a>
              <span>&nbsp;</span>
              <i className="tw-truncate">{eventInfo.event.title}</i>
            </>
        );
    }


    public render(): React.ReactNode {
        const { events, siteSettings } = this.props;
        const { modalVisible, currentEvent } = this.state;

        //the 'events' object is the paginated version, but we need the data.
        //We also need the data to match the full calendar format, do that here
        if (Object.hasOwn(events, 'data')) {
            for (let i = 0; i < events.data.length; i++) {

                //this data is also not consistent across the site, fix that here
                // e.g. 'attending' is different from 'my-events'
                if (typeof events.data[i].event === 'object') {
                    Object.assign(events.data[i], events.data[i].event);
                }

                events.data[i].url = events.data[i].link;
                events.data[i].start = events.data[i].starts_at;
                events.data[i].end = events.data[i].ends_at;
                events.data[i].classNames = ['tw-truncate'];
            }
        }

        return (
            <>
              <FullCalendar
              ref={this.calendarComponentRef}
              initialView={window.innerWidth < 765 ? 'listWeek' : 'dayGridMonth'}
              plugins={[dayGridPlugin, interactionPlugin, listPlugin]}
              eventContent={this.renderEventContent}
              eventClick={(info) => {
                info.jsEvent.preventDefault(); // don't let the browser navigate
                this.setState(() => ({
                    modalVisible: true,
                    currentEvent: info.event,
                  }));
              }}
              events={events ? events.data : []}
              windowResize={this.handleWindowResize}
              />
              {modalVisible
                  ? this.renderModal(currentEvent, events, siteSettings)
                  : null}
            </>
        );
    }

    /**
     * When we click an event, show a nice little modal with the event content
     *
     * @param currentEvent
     * @param events
     * @param siteSettings
     */
    public renderModal(currentEvent, events, siteSettings): React.ReactNode {
        // Find the current event as it has more info than what full cal gives back
        const event = events.data.find((obj) => parseInt(obj.id) === parseInt(currentEvent.id));

        return (
            <>
              <Modal
              onClose={() => {
                this.setState(() => ({
                  modalVisible: false,
                  currentEvent: {},
                }));
              }}
              title={null}
              isNaked={true} //we don't want the modal wrapper, just show the content below 'as is' on the modal backdrop
            >
              <article className="event">
                  <a href={event.url}>
                  <div className="image" style={{ backgroundImage: `url(${event.img})` }}>
                      {event.userTicket ? <div className="attending-banner">You're attending this event</div> : null}
                    </div>
                </a>
                  <div className="eventlocation">
                  <div className="location-detail">
                      <i className="material-icons-outlined">schedule</i>
                      <span>{ moment().format('dddd Do MMM YYYY (h:mm a)') }</span>
                  </div>
                  <div className="location-detail">
                      <i className="material-icons-outlined">place</i>
                      <span>{  this.getAddress(event)  }</span>
                    </div>
                </div>
                  <div className="content">
                  <div className="overview">
                      <h3>
                      <a href={event.url}>
                          {event.status === 'draft'
                      ? <span className="is-draft" />
                      : null}
                          { event.title }
                        </a>
                    </h3>

                      <p className="description">{event.excerpt}</p>

                      <div className="row ">
                      <div className="no-margin no-padding col-sm-8 col-md-8 col-lg-8 col-xs-8 col-lg-8 col-xl-8">
                          {
                        !event.settings.hide_attendees
                        ? (
                          <p className="attendees ">
                            <a href={event.url}>
                              <strong>
                                {event.ticketing_managed_externally
                                  ? event.total_attendees
                                  : event.total_redeemed_tickets}
                                &nbsp;attendees
                              </strong>
                            </a>
                            &nbsp;
                            {event.total_remaining_tickets}
                            &nbsp;tickets left
                          </p>
                          )
                        : null
                      }

                        </div>
                      <div className="no-margin no-padding col-sm-4 col-md-4 col-lg-4 col-xs-4 col-lg-4 col-xl-4">
                          <ul className="social">
                          {
                          event.contactables.facebook
                            ? (
                              <li className="fb">
                                <a href={`https://facebook.com/${event.contactables.facebook}`} title="Open Facebook Page" target="_blank" rel="noreferrer">
                                  <i className="fa fa-facebook-f" />
                                </a>
                              </li>
                            )
                            : null
                        }
                          {
                          event.contactables.facebook
                            ? (
                              <li className="tw">
                                <a href={`https://twitter.com/${event.contactables.twitter}`} title="Open Twitter Account" target="_blank" rel="noreferrer">
                                  <i className="fa fa-twitter" />
                                </a>
                              </li>
                            )
                            : null
                        }
                        </ul>
                        </div>
                    </div>

                    </div>

                  <div className="row">
                      <h4 className="price">{ event.price_formatted }</h4>
                      <div className="eventdetails-cont">
                      <a href={event.url} className="btn-c2a eventdetailsbutton">{ siteSettings.buttons_text.events.view }</a>
                      {
                      siteSettings.display_event_availability
                      ? (
                        <div>
                          {
                            event.user_is_attending
                            ? <p className="btn-c2a eventdetailsbutton attending">{siteSettings.buttons_text.events.availablity.attending}</p>
                            : null
                          }
                          {
                            !event.user_is_attending && event.has_remaining_tickets
                              ? <p className="btn-c2a eventdetailsbutton available">{ siteSettings.buttons_text.events.availablity.available }</p>
                              : null
                          }
                          {
                            !event.user_is_attending && !event.has_remaining_tickets
                              ? <p className="btn-c2a eventdetailsbutton soldout">{ siteSettings.buttons_text.events.availablity.soldOut }</p>
                              : null
                          }
                        </div>
                        )
                      : null
                    }

                    </div>
                    </div>
                </div>
                </article>
            </Modal>
            </>
        );
    }

    /**
     * Gets a pretty address from the event
     * TODO This is duplicated from the angular version, when angular eventually goes, unify this method
     *
     * @param event
     */
    public getAddress(event): string {
        const address = [],
            order = [
                'line_1',
                'line_2',
                'town_city',
                'county',
                'postcode',
            ];
        order.forEach(function (index) {
            if (event.address && event.address[index] !== undefined && event.address[index] !== '') {
                address.push(event.address[index]);
            }
        });
        return address.join(', ');
    }
}

export default EventCalendar;
